import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Cloud, 
  Copy, 
  Check, 
  ChevronRight,
  Sparkles,
  ExternalLink,
  User,
  BookOpen,
  ArrowLeft,
  MousePointer,
  LayoutDashboard,
  FileText,
  Settings,
  Mail,
  Rocket
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

// techbaenae color scheme
const colors = {
  background: '#FFFFFF',
  backgroundAlt: '#F8F9FA',
  teal: '#00CED1',
  pink: '#FF007F',
  purple: '#6A0DAD',
  gold: '#FFD700',
  text: '#333333',
  textLight: '#666666',
  border: '#E5E7EB',
};

// Resource metadata
export const resourcesData = {
  'custom-cursor-effects': {
    id: 'custom-cursor-effects',
    type: 'prompt',
    platform: 'Website',
    title: 'Custom Cursor Effects',
    description: 'Add magical cursor trails, glowing effects, and interactive blobs to make your website memorable.',
    icon: MousePointer,
    color: colors.teal,
    author: {
      name: 'techbaenae',
      handle: '@techbaenae',
      avatar: 'https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg',
      title: 'Data Dreamer'
    },
    tags: ['website', 'design', 'animations', 'ux'],
    relatedResources: ['admin-dashboard', 'getting-started'],
  },
  'admin-dashboard': {
    id: 'admin-dashboard',
    type: 'prompt',
    platform: 'Website',
    title: 'Admin Dashboard & Analytics',
    description: 'Track visitors, clicks, and engagement with a custom admin panel. No external tools needed.',
    icon: LayoutDashboard,
    color: colors.purple,
    author: {
      name: 'techbaenae',
      handle: '@techbaenae',
      avatar: 'https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg',
      title: 'Data Dreamer'
    },
    tags: ['website', 'analytics', 'admin', 'tracking'],
    relatedResources: ['custom-cursor-effects', 'content-management'],
  },
  'getting-started': {
    id: 'getting-started',
    type: 'prompt',
    platform: 'Website',
    title: 'Getting Started - Website Foundation',
    description: 'The essential prompts to build your personal brand website from scratch.',
    icon: Rocket,
    color: colors.gold,
    author: {
      name: 'techbaenae',
      handle: '@techbaenae',
      avatar: 'https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg',
      title: 'Data Dreamer'
    },
    tags: ['website', 'beginner', 'foundation', 'personal-brand'],
    relatedResources: ['custom-cursor-effects', 'admin-dashboard'],
  },
  'content-management': {
    id: 'content-management',
    type: 'prompt',
    platform: 'Website',
    title: 'Content Management System',
    description: 'Make your website content editable without touching code. Add blogs, update text, manage posts.',
    icon: FileText,
    color: colors.pink,
    author: {
      name: 'Community',
      handle: '@emergent',
      avatar: '',
      title: 'Builder Resource'
    },
    tags: ['website', 'cms', 'blog', 'content'],
    relatedResources: ['admin-dashboard', 'cloudinary-setup'],
  },
  'cloudinary-setup': {
    id: 'cloudinary-setup',
    type: 'guide',
    platform: 'Website',
    title: 'Cloudinary Setup Guide',
    description: 'Complete guide to integrate Cloudinary for persistent image storage. Production-tested on Emergent.',
    icon: Cloud,
    color: '#00CED1',
    author: {
      name: 'techbaenae',
      handle: '@techbaenae',
      avatar: 'https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg',
      title: 'Data Dreamer'
    },
    tags: ['website', 'cloudinary', 'images', 'storage', 'integration'],
    relatedResources: ['content-management', 'admin-dashboard'],
  },
  'add-on-features': {
    id: 'add-on-features',
    type: 'prompt',
    platform: 'Website',
    title: 'Add-On Features',
    description: 'Contact forms, testimonials, dark mode, announcement banners, and more website enhancements.',
    icon: Settings,
    color: colors.teal,
    author: {
      name: 'techbaenae',
      handle: '@techbaenae',
      avatar: 'https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg',
      title: 'Data Dreamer'
    },
    tags: ['website', 'features', 'contact', 'testimonials'],
    relatedResources: ['content-management', 'getting-started'],
  },
  'backend-setup': {
    id: 'backend-setup',
    type: 'guide',
    platform: 'Website',
    title: 'Backend & Domain Setup',
    description: 'Step-by-step guide to connect your domain and get your website live.',
    icon: Settings,
    color: colors.teal,
    author: {
      name: 'techbaenae',
      handle: '@techbaenae',
      avatar: 'https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg',
      title: 'Data Dreamer'
    },
    tags: ['website', 'domain', 'dns', 'setup'],
    relatedResources: ['getting-started', 'add-on-features'],
  },
  'more-prompts': {
    id: 'more-prompts',
    type: 'prompt',
    platform: 'Website',
    title: 'More Prompt Ideas',
    description: 'A collection of prompt starters for different use cases and industries.',
    icon: Rocket,
    color: colors.gold,
    author: {
      name: 'techbaenae',
      handle: '@techbaenae',
      avatar: 'https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg',
      title: 'Data Dreamer'
    },
    tags: ['website', 'prompts', 'collection', 'ideas'],
    relatedResources: ['getting-started', 'custom-cursor-effects'],
  },
};

// Email capture modal
const EmailCaptureModal = ({ isOpen, onClose, onSubmit, resourceTitle }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await onSubmit(email);
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-md rounded-3xl p-8 bg-white relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>

          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${colors.teal}20` }}>
              <Cloud className="w-8 h-8" style={{ color: colors.teal }} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Save to Your Cloud</h3>
            <p className="text-gray-500 text-sm">
              Enter your email to save "{resourceTitle}" and access all your downloads in one place.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 bg-white placeholder-gray-400"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 rounded-xl font-medium text-white transition-all hover:opacity-90 disabled:opacity-50"
              style={{ background: colors.teal }}
            >
              {isSubmitting ? 'Saving...' : 'Save & Continue'}
            </button>
          </form>

          <p className="text-gray-400 text-xs text-center mt-4">
            We'll send you a link to access this resource anytime.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Resource preview modal (before accessing)
const ResourcePreviewModal = ({ isOpen, onClose, resource, onSaveToCloud, userEmail }) => {
  if (!isOpen || !resource) return null;

  const Icon = resource.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="w-full max-w-lg rounded-3xl overflow-hidden bg-white relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Cover */}
          <div 
            className="h-48 relative flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${resource.color}20 0%, ${resource.color}40 100%)` }}
          >
            <Icon className="w-20 h-20" style={{ color: resource.color }} strokeWidth={1.5} />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            {/* Type badge */}
            <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 text-xs font-medium" style={{ color: resource.color }}>
              {resource.platform} Prompt
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{resource.title}</h2>
            <p className="text-gray-600 mb-6">{resource.description}</p>

            {/* Author */}
            <div className="flex items-center gap-3 mb-6 p-3 rounded-xl bg-gray-50">
              {resource.author.avatar ? (
                <img 
                  src={resource.author.avatar} 
                  alt={resource.author.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <span className="text-purple-600 font-bold text-sm">{resource.author.name?.charAt(0) || 'C'}</span>
                </div>
              )}
              <div>
                <p className="font-medium text-gray-900">{resource.author.handle}</p>
                <p className="text-sm text-gray-500">{resource.author.title}</p>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {resource.tags.map((tag) => (
                <span 
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* CTA */}
            <button
              onClick={onSaveToCloud}
              className="w-full py-4 rounded-xl font-medium text-white flex items-center justify-center gap-2 transition-all hover:opacity-90"
              style={{ background: colors.teal }}
            >
              <Cloud className="w-5 h-5" />
              {userEmail ? 'View Resource' : 'Save to Your Cloud'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Copy button component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="absolute top-3 right-3 p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
    >
      {copied ? (
        <Check className="w-4 h-4 text-green-400" />
      ) : (
        <Copy className="w-4 h-4 text-gray-300" />
      )}
    </button>
  );
};

// Prompt block component
const PromptBlock = ({ title, prompt, description }) => (
  <div className="mb-8">
    {title && <h4 className="text-lg font-semibold text-gray-900 mb-2">{title}</h4>}
    {description && <p className="text-gray-600 text-sm mb-3">{description}</p>}
    <div className="relative">
      <pre className="bg-gray-900 text-gray-100 p-4 pr-12 rounded-xl overflow-x-auto text-sm leading-relaxed whitespace-pre-wrap">
        {prompt}
      </pre>
      <CopyButton text={prompt} />
    </div>
  </div>
);

// Emergent CTA component
const EmergentCTA = () => (
  <div className="my-12 p-6 rounded-2xl border-2 border-dashed" style={{ borderColor: colors.teal, background: `${colors.teal}08` }}>
    <div className="flex flex-col md:flex-row items-center gap-6">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5" style={{ color: colors.teal }} />
          <span className="text-sm font-medium" style={{ color: colors.teal }}>Built with Emergent</span>
        </div>
        <h4 className="text-xl font-bold text-gray-900 mb-2">Ready to build your own?</h4>
        <p className="text-gray-600 text-sm">
          These prompts work best with Emergent AI.
        </p>
      </div>
      <a
        href="https://app.emergent.sh/?via=naomi"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-white whitespace-nowrap transition-all hover:opacity-90"
        style={{ background: colors.teal }}
      >
        Try Emergent
        <ExternalLink className="w-4 h-4" />
      </a>
    </div>
  </div>
);

// About Emergent section component (for bottom of pages)
const AboutEmergent = () => (
  <div className="mt-16 pt-12 border-t border-gray-200">
    <div className="rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)' }}>
      {/* Header */}
      <div className="p-8 md:p-12">
        <div className="flex items-center gap-3 mb-6">
          {/* Emergent Logo */}
          <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center">
            <span className="text-2xl font-bold text-black">E</span>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-white">About Emergent</h3>
            <p className="text-gray-400 text-sm">The AI platform behind these prompts</p>
          </div>
        </div>

        <p className="text-gray-300 text-lg mb-8 max-w-2xl">
          Emergent is an AI-powered platform that turns your ideas into fully functional websites, apps, and tools. 
          No coding required. Just describe what you want, and watch it come to life.
        </p>

        {/* Features grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
            <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center mb-3">
              <Sparkles className="w-5 h-5 text-teal-400" />
            </div>
            <h4 className="font-semibold text-white mb-2">Prompt to Product</h4>
            <p className="text-sm text-gray-400">Describe your vision in plain English. Emergent handles the rest.</p>
          </div>
          <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center mb-3">
              <BookOpen className="w-5 h-5 text-purple-400" />
            </div>
            <h4 className="font-semibold text-white mb-2">Full-Stack Apps</h4>
            <p className="text-sm text-gray-400">Build complete websites with databases, auth, and admin panels.</p>
          </div>
          <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
            <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center mb-3">
              <User className="w-5 h-5 text-pink-400" />
            </div>
            <h4 className="font-semibold text-white mb-2">No Code Needed</h4>
            <p className="text-sm text-gray-400">Perfect for creators, founders, and anyone with an idea.</p>
          </div>
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <a
            href="https://app.emergent.sh/?via=naomi"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-black bg-white hover:bg-gray-100 transition-colors"
          >
            Start Building for Free
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  </div>
);

// Related resources component
const RelatedResources = ({ resourceIds }) => {
  const resources = resourceIds.map(id => resourcesData[id]).filter(Boolean);
  
  if (resources.length === 0) return null;

  return (
    <div className="mt-12 pt-12 border-t border-gray-200">
      <h3 className="text-xl font-bold text-gray-900 mb-6">More Resources</h3>
      <div className="grid md:grid-cols-2 gap-4">
        {resources.map((resource) => {
          const Icon = resource.icon;
          return (
            <Link
              key={resource.id}
              to={`/resources/prompts/${resource.id}`}
              className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all"
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${resource.color}20` }}
              >
                <Icon className="w-6 h-6" style={{ color: resource.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 truncate">{resource.title}</h4>
                <p className="text-sm text-gray-500">{resource.platform} Prompt</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
          );
        })}
      </div>
    </div>
  );
};

// Author card component
const AuthorCard = ({ author }) => (
  <div className="mt-12 p-6 rounded-2xl bg-gray-50">
    <div className="flex items-center gap-4">
      {author.avatar ? (
        <img 
          src={author.avatar} 
          alt={author.name}
          className="w-16 h-16 rounded-full object-cover"
        />
      ) : (
        <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
          <span className="text-purple-600 font-bold text-xl">{author.name?.charAt(0) || 'C'}</span>
        </div>
      )}
      <div>
        <p className="font-bold text-gray-900">{author.handle}</p>
        <p className="text-gray-500">{author.title}</p>
      </div>
    </div>
    <p className="mt-4 text-gray-600 text-sm">
      Sharing prompts, templates, and resources to help you build with AI. Follow for more brain food.
    </p>
    <div className="mt-4 flex gap-3">
      <a 
        href="https://instagram.com/techbaenae" 
        target="_blank" 
        rel="noopener noreferrer"
        className="w-10 h-10 rounded-lg bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
        title="Instagram"
      >
        <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      </a>
      <a 
        href="https://www.linkedin.com/in/techbaenae/" 
        target="_blank" 
        rel="noopener noreferrer"
        className="w-10 h-10 rounded-lg bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
        title="LinkedIn"
      >
        <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      </a>
      <a 
        href="https://techbaenae.com" 
        target="_blank" 
        rel="noopener noreferrer"
        className="w-10 h-10 rounded-lg bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
        title="Website"
      >
        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/>
        </svg>
      </a>
    </div>
  </div>
);

// Export utility functions
export const useUserCloud = () => {
  const [userEmail, setUserEmail] = useState(null);
  const [savedResources, setSavedResources] = useState([]);

  useEffect(() => {
    const email = localStorage.getItem('datadreaming_user_email');
    const saved = JSON.parse(localStorage.getItem('datadreaming_saved_resources') || '[]');
    if (email) setUserEmail(email);
    setSavedResources(saved);
  }, []);

  const saveUser = (email) => {
    localStorage.setItem('datadreaming_user_email', email);
    setUserEmail(email);
  };

  const saveResource = (resourceId) => {
    const updated = [...new Set([...savedResources, resourceId])];
    localStorage.setItem('datadreaming_saved_resources', JSON.stringify(updated));
    setSavedResources(updated);
  };

  return { userEmail, savedResources, saveUser, saveResource };
};

export { 
  EmailCaptureModal, 
  ResourcePreviewModal, 
  PromptBlock, 
  EmergentCTA,
  AboutEmergent,
  RelatedResources, 
  AuthorCard,
  colors as resourceColors 
};
